import React, { useState } from 'react';

/**
 * Star rating input component for feedback forms
 * @param {Object} props - Component props
 * @param {number} props.value - Current rating value (1-5)
 * @param {Function} props.onChange - Callback when rating changes
 * @param {string} props.label - Label text for the rating
 * @param {number} [props.maxRating=5] - Maximum rating value
 * @param {boolean} [props.required=false] - Whether the rating is required
 * @param {string} [props.name] - Input name for form handling
 * @returns {JSX.Element} RatingInput component
 */
const RatingInput = ({
  value = 0,
  onChange,
  label,
  maxRating = 5,
  required = false,
  name
}) => {
  const [hoverValue, setHoverValue] = useState(0);

  const handleClick = (rating) => {
    if (onChange) {
      onChange(rating);
    }
  };

  const handleKeyDown = (e, rating) => {
    // Support keyboard navigation
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(rating);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      const newValue = Math.min(rating + 1, maxRating);
      handleClick(newValue);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      const newValue = Math.max(rating - 1, 1);
      handleClick(newValue);
    }
  };

  const renderStar = (rating) => {
    const isFilled = rating <= (hoverValue || value);
    const isHovered = rating <= hoverValue && hoverValue > 0;

    return (
      <button
        key={rating}
        type="button"
        className={`feedback-star ${isFilled ? 'filled' : ''} ${isHovered ? 'hovered' : ''}`}
        onClick={() => handleClick(rating)}
        onMouseEnter={() => setHoverValue(rating)}
        onMouseLeave={() => setHoverValue(0)}
        onKeyDown={(e) => handleKeyDown(e, rating)}
        aria-label={`Rate ${rating} out of ${maxRating} stars`}
        aria-pressed={rating === value}
        tabIndex={rating === 1 || rating === value ? 0 : -1}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill={isFilled ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      </button>
    );
  };

  const getRatingLabel = () => {
    if (hoverValue > 0) return getRatingText(hoverValue);
    if (value > 0) return getRatingText(value);
    return 'Not rated';
  };

  const getRatingText = (rating) => {
    const labels = {
      1: 'Poor',
      2: 'Fair',
      3: 'Good',
      4: 'Very Good',
      5: 'Excellent'
    };
    return labels[rating] || '';
  };

  return (
    <div className="feedback-rating-input">
      {label && (
        <label className="feedback-rating-label">
          {label}
          {required && <span className="required-indicator" aria-hidden="true">*</span>}
        </label>
      )}
      <div
        className="feedback-stars-container"
        role="radiogroup"
        aria-label={label || 'Rating'}
      >
        {Array.from({ length: maxRating }, (_, i) => renderStar(i + 1))}
        <span className="feedback-rating-text" aria-live="polite">
          {getRatingLabel()}
        </span>
      </div>
      {/* Hidden input for form submission */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={value || ''}
        />
      )}
    </div>
  );
};

export default RatingInput;
